import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Clock, ShieldCheck, Eye, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, MaskLine } from "@/components/Reveal";
import HeroVisual from "@/components/HeroVisual";
import Simulator from "@/components/Simulator";
import Faq from "@/components/Faq";
import ArticleCard from "@/components/ArticleCard";
import ContactBlock from "@/components/ContactBlock";
import { PhoneCTA, WhatsAppCTA } from "@/components/ContactButtons";
import { SERVICE_STEPS, HOW_STEPS, ARTICLES } from "@/data/content";

const REASSURANCE = ["4 % TVAC", "Suivi des rejets", "Reporting clair", "Interlocuteur identifié"];

const BENEFITS = [
  {
    icon: Clock,
    title: "Gagnez du temps",
    text: "Nous prenons en charge l'encodage, la transmission et le suivi de votre facturation.",
  },
  {
    icon: ShieldCheck,
    title: "Limitez les pertes",
    text: "Les rejets et anomalies administratives sont identifiés, corrigés et suivis.",
  },
  {
    icon: Eye,
    title: "Gardez une vision claire",
    text: "Vous recevez un suivi compréhensible des montants transmis, acceptés ou en attente.",
  },
];

const Home = () => (
  <>
    <Seo
      title="AVELIS | Tarification pour infirmiers indépendants en Belgique"
      description="AVELIS prend en charge la tarification, le suivi et les corrections administratives des infirmiers indépendants en Belgique. Tarif clair : 4 % TVAC."
    />

    {/* HERO */}
    <section data-testid="hero" className="relative overflow-hidden bg-[#f6f3ec] pt-[130px] sm:pt-[150px] pb-16 sm:pb-20">
      <div className="absolute top-0 right-0 h-full w-1/2 dotted-bg opacity-40 hidden lg:block" aria-hidden="true" />
      <div className="container-avelis relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: "easeOut" }}>
              <span data-testid="hero-badge" className="inline-flex items-center gap-2.5 rounded-full border border-[#176b70]/25 bg-white/60 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#176b70]">
                <span className="dot-sage" />
                Tarification infirmière • Belgique
              </span>
            </motion.div>

            <h1 data-testid="hero-title" className="mt-7 text-[2.6rem] sm:text-6xl lg:text-[4.3rem] font-extrabold tracking-tight leading-[1.04] text-[#0d2731]">
              <MaskLine delay={0.15}>Vous soignez.</MaskLine>
              <MaskLine delay={0.32}>
                <span className="text-[#176b70]">Nous tarifons.</span>
              </MaskLine>
            </h1>

            <motion.p
              data-testid="hero-subtitle"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: "easeOut" }}
              className="mt-6 max-w-xl text-base sm:text-lg text-[#4b585e] leading-relaxed"
            >
              AVELIS prend en charge votre tarification, le suivi des retours et les corrections administratives. Un service clair à <strong className="text-[#0d2731]">4 % TVAC</strong> des honoraires encaissés, auxquels s'ajoutent les frais de logiciel.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: "easeOut" }}
              className="mt-9 flex flex-col sm:flex-row gap-3.5"
            >
              <PhoneCTA location="hero" />
              <WhatsAppCTA location="hero" />
            </motion.div>

            <motion.p
              data-testid="hero-reassurance"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-6 text-xs sm:text-sm font-semibold text-[#4b585e] flex items-center gap-2 flex-wrap"
            >
              <span className="dot-sage" />
              Premier échange gratuit • Sans engagement
            </motion.p>
          </div>

          <div className="lg:col-span-6">
            <HeroVisual />
          </div>
        </div>
      </div>
    </section>

    {/* RÉASSURANCE */}
    <section data-testid="reassurance-bar" className="border-y border-[#e5e1d8] bg-white/50">
      <div className="container-avelis grid grid-cols-2 lg:grid-cols-4">
        {REASSURANCE.map((r, i) => (
          <Reveal key={r} delay={i * 0.08} className={`px-2 py-7 sm:py-9 text-center ${i > 0 ? "border-l border-[#e5e1d8]" : ""} ${i === 2 ? "max-lg:border-l-0 max-lg:border-t" : ""} ${i === 3 ? "max-lg:border-t" : ""}`}>
            <p data-testid={`reassurance-${i}`} className="text-base sm:text-lg font-extrabold tracking-tight text-[#0d2731]">{r}</p>
          </Reveal>
        ))}
      </div>
    </section>

    {/* BÉNÉFICES */}
    <section id="avantages" data-testid="benefits-section" className="py-16 sm:py-24 lg:py-28 scroll-mt-24">
      <div className="container-avelis">
        <div className="max-w-2xl">
          <Reveal>
            <p className="eyebrow"><span className="dot-sage" />Pourquoi AVELIS</p>
            <h2 className="h2-avelis mt-4">Moins d'administratif. Plus de temps pour vos patients.</h2>
            <p className="lead-avelis mt-5">
              AVELIS organise et suit votre tarification afin que vous puissiez vous concentrer sur votre activité de soins.
            </p>
          </Reveal>
        </div>
        <div className="mt-12 sm:mt-14 grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6 max-w-5xl">
          {BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08}>
              <div data-testid={`benefit-card-${i}`} className="card-soft card-soft-hover h-full p-7 bg-white">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#176b70]/10 text-[#176b70]">
                  <b.icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 text-lg font-extrabold tracking-tight text-[#0d2731]">{b.title}</h3>
                <p className="mt-2.5 text-sm text-[#4b585e] leading-relaxed">{b.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* SERVICE */}
    <section id="service" data-testid="service-section" className="py-16 sm:py-24 lg:py-28 bg-[#f9f8f5] border-y border-[#e5e1d8] scroll-mt-24">
      <div className="container-avelis">
        <div className="max-w-2xl">
          <Reveal>
            <p className="eyebrow"><span className="dot-sage" />Le service</p>
            <h2 className="h2-avelis mt-4">Votre tarification, suivie de A à Z.</h2>
          </Reveal>
        </div>
        <ol className="mt-12 sm:mt-14 max-w-3xl">
          {SERVICE_STEPS.map((step, i) => (
            <Reveal key={step} delay={Math.min(i * 0.06, 0.3)}>
              <li data-testid={`service-step-${i + 1}`} className="flex items-baseline gap-5 py-5 border-b border-[#e5e1d8]">
                <span className="text-sm font-extrabold text-[#b5c4b1] tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-base sm:text-lg font-semibold text-[#0d2731]">{step}</span>
              </li>
            </Reveal>
          ))}
        </ol>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-xl text-base sm:text-lg text-[#4b585e] leading-relaxed">
            Vous gardez la maîtrise de votre activité. <strong className="text-[#0d2731]">AVELIS prend en charge le processus administratif convenu avec vous.</strong>
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3.5">
            <PhoneCTA location="service" />
            <WhatsAppCTA location="service" />
          </div>
        </Reveal>
      </div>
    </section>

    {/* SIMULATEUR */}
    <Simulator />

    {/* FONCTIONNEMENT */}
    <section id="fonctionnement" data-testid="how-section" className="py-16 sm:py-24 lg:py-28 scroll-mt-24">
      <div className="container-avelis">
        <div className="max-w-2xl">
          <Reveal>
            <p className="eyebrow"><span className="dot-sage" />Comment ça fonctionne</p>
            <h2 className="h2-avelis mt-4">Commencer avec AVELIS, simplement.</h2>
          </Reveal>
        </div>
        <div className="mt-12 sm:mt-14 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {HOW_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div data-testid={`how-step-${i}`} className="relative pt-8 border-t-2 border-[#e5e1d8]">
                <span className="absolute -top-[2px] left-0 h-[2px] w-14 bg-[#b5c4b1]" aria-hidden="true" />
                <p className="text-4xl font-extrabold tracking-tight text-[#b5c4b1]">{s.n}</p>
                <h3 className="mt-4 text-lg font-extrabold tracking-tight text-[#0d2731]">{s.title}</h3>
                <p className="mt-2.5 text-sm text-[#4b585e] leading-relaxed">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* À PROPOS */}
    <section id="a-propos" data-testid="about-section" className="py-16 sm:py-24 bg-[#f9f8f5] border-y border-[#e5e1d8] scroll-mt-24">
      <div className="container-avelis">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <span className="dot-sage !h-2.5 !w-2.5 mx-auto" aria-hidden="true" />
            <h2 className="h2-avelis mt-6">Un interlocuteur qui comprend votre activité.</h2>
            <p className="lead-avelis mt-6">
              AVELIS accompagne les infirmiers indépendants et les équipes infirmières dans la gestion et le suivi de leur tarification. Notre approche repose sur trois principes : clarté, réactivité et relation directe.
            </p>
          </Reveal>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <Faq />

    {/* CONSEILS */}
    <section id="conseils" data-testid="home-conseils" className="py-16 sm:py-24 scroll-mt-24">
      <div className="container-avelis">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
          <Reveal>
            <p className="eyebrow"><span className="dot-sage" />Conseils</p>
            <h2 className="h2-avelis mt-4 max-w-xl">Nos conseils pour mieux gérer votre facturation</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/conseils" data-testid="home-conseils-link" className="inline-flex items-center gap-2 text-sm font-bold text-[#176b70] hover:text-[#0d2731] transition-colors shrink-0">
              Voir tous les conseils
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {ARTICLES.slice(0, 3).map((a, i) => (
            <Reveal key={a.slug} delay={i * 0.08}>
              <ArticleCard article={a} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CONTACT FINAL */}
    <ContactBlock withId />
  </>
);

export default Home;
